import { parseISO } from "date-fns";

import type { BookableAssetDetailDto, BookableDayDto } from "@/api/types";
import { isDefined } from "@/helpers/util";
import type { BookableDay, Specification } from "@/modules/bookings/constants";
import {
  daysOptions,
  defaultAmountSeats,
  defaultBookableDayEndTime,
  defaultBookableDayStartTime,
} from "@/modules/bookings/constants";
import {
  getMinutesOfDayFromTime,
  getTimeFromMinutesOfDay,
  parseTimeslotAsAmountMinutes,
} from "@/modules/bookings/helpers";

import type { AssetObserver, CreateOrEditAssetFormValues } from "./types";

export const formatBookableDaysPayload = (bookableDays: BookableDay[]): BookableDayDto[] => {
  return [...bookableDays].map((currBookableDay) => {
    const currBookableTimes = currBookableDay.times.filter(
      (currBookableTime) => isDefined(currBookableTime.startTime) && isDefined(currBookableTime.endTime),
    ) as { startTime: number; endTime: number }[];

    return {
      ...currBookableDay,
      times: currBookableTimes.map((currBookableTime) => ({
        startTime: getTimeFromMinutesOfDay(currBookableTime.startTime),
        endTime: getTimeFromMinutesOfDay(currBookableTime.endTime),
      })),
    };
  });
};

export const getDefaultFormValues = (
  assetObservers: AssetObserver[],
  userEmail: string,
  assetDetails?: BookableAssetDetailDto,
): CreateOrEditAssetFormValues => {
  return {
    audience: assetDetails?.audience || [],
    assetObserver: assetDetails?.projectConnection
      ? assetObservers.find((p) => p.id === assetDetails.projectConnection?.id)!
      : assetObservers[0],
    name: assetDetails?.name || "",
    description: assetDetails?.description || "",
    images: assetDetails?.images ?? [],
    specifications: mapAvailableSpecifications({
      sizeSpecification: assetDetails?.sizeSpecification,
      pricePerHourSpecification: assetDetails?.pricePerHourSpecification,
      capacitySpecification: assetDetails?.capacitySpecification,
      locationSpecification: assetDetails?.locationSpecification,
    }),
    sizeSpecification: assetDetails?.sizeSpecification,
    pricePerHourSpecification: assetDetails?.pricePerHourSpecification?.toString(),
    capacitySpecification: assetDetails?.capacitySpecification?.toString(),
    locationSpecification: assetDetails?.locationSpecification,
    regulationsApplicable: !!assetDetails?.regulationDocument,
    regulationDocument: assetDetails?.regulationDocument ? [assetDetails.regulationDocument] : [],
    bookableDays: mapBookableDays(assetDetails?.bookableDays || []),
    timeslot: assetDetails?.timeslot,
    customTimeslot:
      (assetDetails &&
        parseTimeslotAsAmountMinutes(assetDetails.timeslot, Number(assetDetails.customTimeslot || 10))) ||
      10,
    canBookMultipleSlots:
      assetDetails?.seats && assetDetails.seats > 1 ? false : (assetDetails?.canBookMultipleSlots ?? true),
    notificationEnabled: assetDetails?.notificationEnabled ?? false,
    notificationEmail: assetDetails?.notificationEmail ?? userEmail,
    canBookSameDay: assetDetails?.canBookSameDay ?? true,
    maxDaysInAdvance: assetDetails?.maxDaysInAdvance?.toString(),
    hoursForMutations: assetDetails?.hoursForMutations?.toString(),
    requireBookingReason: assetDetails?.requireBookingReason ?? false,
    publishAt: assetDetails?.publishAt ? parseISO(assetDetails?.publishAt) : undefined,
    unpublishAt: assetDetails?.unpublishAt ? parseISO(assetDetails?.unpublishAt) : undefined,
    availableFrom: assetDetails?.availableFrom ? parseISO(assetDetails?.availableFrom) : undefined,
    seats: assetDetails?.seats || defaultAmountSeats,
    externalBookingProvider:
      assetDetails?.externalSyncEmail &&
      (!assetDetails.externalBookingProvider || assetDetails.externalBookingProvider === "none")
        ? "outlook"
        : (assetDetails?.externalBookingProvider ?? "none"),
    externalResourceId: assetDetails?.externalResourceId ?? assetDetails?.externalSyncEmail ?? "",
  };
};

const mapAvailableSpecifications = ({
  sizeSpecification,
  pricePerHourSpecification,
  capacitySpecification,
  locationSpecification,
}: {
  sizeSpecification?: string;
  pricePerHourSpecification?: number;
  capacitySpecification?: number;
  locationSpecification?: string;
}) => {
  const specifications: Specification[] = [];
  if (sizeSpecification) {
    specifications.push("sizeSpecification");
  }
  if (pricePerHourSpecification) {
    specifications.push("pricePerHourSpecification");
  }
  if (capacitySpecification) {
    specifications.push("capacitySpecification");
  }
  if (locationSpecification) {
    specifications.push("locationSpecification");
  }

  return specifications;
};

const mapBookableDays = (bookableDays: BookableAssetDetailDto["bookableDays"]): BookableDay[] => {
  if (bookableDays.length === 0) {
    return daysOptions.map((d) => ({
      day: d,
      allDay: false,
      enabled: true,
      times: [
        {
          startTime: getMinutesOfDayFromTime(defaultBookableDayStartTime, "start"),
          endTime: getMinutesOfDayFromTime(defaultBookableDayEndTime, "end"),
        },
      ],
    }));
  }

  return bookableDays
    .toSorted(
      (bookableDayA, bookableDayB) => daysOptions.indexOf(bookableDayA.day) - daysOptions.indexOf(bookableDayB.day),
    )
    .map((currBookableDay) => {
      const bookableTimes = currBookableDay.times
        ? currBookableDay.times.map((currBookableTime) => {
            return {
              startTime: getMinutesOfDayFromTime(currBookableTime.startTime, "start"),
              endTime: getMinutesOfDayFromTime(currBookableTime.endTime, "end"),
            };
          })
        : [];

      return {
        ...currBookableDay,
        times: bookableTimes,
      };
    });
};
